import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { BookOpen, GraduationCap, Sparkles, ArrowRight, Users, Loader2 } from "lucide-react";

/**
 * Public landing after an LTI 1.3 launch. The user arrives here with:
 *  - A valid Lectura JWT cookie (set by /api/lti/launch)
 *  - Query params: ctx (context_id), title (course title), role
 *
 * We show a friendly "You just launched from Canvas — welcome" state and
 * route the user into the right part of the app based on role.
 */
export const LTILaunch = () => {
  const [params] = useSearchParams();
  const [ctx] = useState(params.get("ctx") || "");
  const [title] = useState(params.get("title") || "your course");
  const [role] = useState(params.get("role") || "student");
  const [count, setCount] = useState(3);

  useEffect(() => {
    if (count <= 0) {
      window.location.href = role === "instructor" ? "/#instructor" : "/#classes";
      return;
    }
    const id = setTimeout(() => setCount((c) => c - 1), 1000);
    return () => clearTimeout(id);
  }, [count, role]);

  return (
    <div className="min-h-screen grain grid place-items-center px-6 py-14 bg-gradient-to-br from-primary via-primary/90 to-primary/70 text-primary-foreground">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-lg w-full rounded-3xl bg-background text-foreground shadow-2xl overflow-hidden"
        data-testid="lti-launch-card"
      >
        <div className="p-8 sm:p-10 text-center">
          <div className="mx-auto grid place-items-center h-16 w-16 rounded-2xl bg-accent/15 text-accent">
            <Sparkles className="h-8 w-8" />
          </div>
          <p className="mt-5 text-[11px] font-bold uppercase tracking-[0.25em] text-accent">
            Launched from Canvas
          </p>
          <h1 className="mt-2 font-serif text-3xl sm:text-4xl font-bold leading-tight">
            Welcome to Lectura
          </h1>
          <p className="mt-3 text-sm text-muted-foreground">
            You launched into <span className="font-semibold text-foreground">{title}</span> as a
            <span className="ml-1 font-semibold text-foreground">{role === "instructor" ? "instructor" : "student"}</span>.
          </p>
          <div className="mt-6 flex items-center justify-center gap-2 text-xs text-muted-foreground">
            <span className="rounded-full bg-secondary px-3 py-1 font-mono">Context: {ctx || "—"}</span>
          </div>
          <div className="mt-8 rounded-2xl bg-secondary/50 border-l-2 border-accent p-4 text-left">
            <p className="text-[11px] font-bold uppercase tracking-widest text-accent">
              {role === "instructor" ? "Instructor mode" : "Student mode"}
            </p>
            <p className="mt-1 text-sm text-foreground/80 leading-relaxed">
              {role === "instructor"
                ? "You'll land on the Instructor panel — create classes, upload chapters, push assignments and sync grades back to Canvas."
                : "You'll land on your Classes — see assignments your teacher posted, and start studying."}
            </p>
          </div>
          <Link
            to={role === "instructor" ? "/" : "/"}
            data-testid="lti-continue-btn"
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-accent text-accent-foreground px-8 py-3.5 font-semibold hover:opacity-90 transition"
          >
            <ArrowRight className="h-4 w-4" /> Continue to Lectura {count > 0 && `(auto in ${count}s)`}
          </Link>
        </div>
        <div className="border-t border-border bg-secondary/30 px-8 py-4 text-center">
          <p className="text-xs text-muted-foreground flex items-center justify-center gap-1.5">
            <BookOpen className="h-3.5 w-3.5 text-accent" />
            Lectura · Study smarter. Score higher.
          </p>
        </div>
      </motion.div>
    </div>
  );
};
